import { isPlainObject, isArray } from 'lodash-es'

import { RouteObject } from '@/router/types'

/**
 * @description 获取localStorage
 * @param {String} key Storage名称
 * @return string
 */
export const localGet = (key: string) => {
  const value = window.localStorage.getItem(key)
  try {
    return JSON.parse(window.localStorage.getItem(key) as string)
  } catch (error) {
    console.warn(error)
    return value
  }
}

/**
 * @description 存储localStorage
 * @param {String} key Storage名称
 * @param {Any} value Storage值
 * @return void
 */
export const localSet = (key: string, value: any) => {
  window.localStorage.setItem(key, JSON.stringify(value))
}

/**
 * @description 清除localStorage
 * @param {String} key Storage名称
 * @return void
 */
export const localRemove = (key: string) => {
  window.localStorage.removeItem(key)
}

/**
 * @description 清除所有localStorage
 * @return void
 */
export const localClear = () => {
  window.localStorage.clear()
}

/**
 * @description 递归查询对应的路由
 * @param {String} path 当前访问地址
 * @param {Array} routes 路由列表
 * @returns array
 */
export const searchRoute = (path: string, routes: RouteObject[] = []): RouteObject => {
  let result: RouteObject = {}
  for (const item of routes) {
    if (item.path?.toLocaleLowerCase() === path?.toLocaleLowerCase()) return item
    if (item.children) {
      const res = searchRoute(path, item.children)
      if (Object.keys(res).length) result = res
    }
  }
  return result
}

// 递归处理树数据，参数为 数据 和 childrenKey, 返回数据为 数据 的 childs 字段
export const dealTreeData = (data: any, childrenKey: string) => {
  if (!data?.length || !childrenKey) {
    return data
  }
  return data.map((item: any) => {
    return { ...item, childs: dealTreeData(item[childrenKey], childrenKey) }
  })
}

/**
 * 在对象中递归查找多个指定属性名的值（每个属性只返回第一个匹配值）
 * @param obj 要查找的对象
 * @param keyNames 要查找的属性名数组
 * @returns 找到的值的对象
 */
export function findValuesByKeys<T extends string>(obj: unknown, keyNames: T[]): Record<T, any> {
  const result = {} as Record<T, any>
  const foundKeys = new Set<T>()

  if (!isPlainObject(obj) && !isArray(obj)) return result

  const dealObj = (obj: Record<string, unknown>) => {
    const subResult = findValuesByKeys(obj, keyNames.filter((key) => !foundKeys.has(key)) as T[])
    for (const [key, value] of Object.entries(subResult)) {
      const typedKey = key as T
      if (!foundKeys.has(typedKey)) {
        result[typedKey] = value
        foundKeys.add(typedKey)
      }
    }
  }

  if (isArray(obj)) {
    for (const item of obj) {
      dealObj(item)
    }
  } else if (isPlainObject(obj)) {
    for (const key in obj as any) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue
      const item = (obj as Record<string, any>)[key]

      if (keyNames.includes(key as T) && !foundKeys.has(key as T)) {
        result[key as T] = item
        foundKeys.add(key as T)
      }

      if (foundKeys.size < keyNames.length) {
        dealObj(item)
      }
    }
  }

  return result
}

/** 颜色转换为rgba */
export function colorToRgba(colorString: string, alpha = 1) {
  if (typeof colorString !== 'string') return ''
  const color = colorString.trim().toLowerCase()

  const formatRgba = (red: number, green: number, blue: number) =>
    `rgba(${red},${green},${blue},${alpha})`

  try {
    if (color.startsWith('#')) {
      let hex = color.slice(1)
      if (hex.length === 3) {
        hex = [...hex].map((ch) => ch + ch).join('')
      }
      if (hex.length !== 6) return ''
      const numericValue = parseInt(hex, 16)
      if (isNaN(numericValue)) return ''
      const red = (numericValue >> 16) & 255
      const green = (numericValue >> 8) & 255
      const blue = numericValue & 255
      return formatRgba(red, green, blue)
    }

    const matchedNumbers = color.match(/\d+/g)
    if (!matchedNumbers || matchedNumbers.length < 3) return ''
    const [redValue, greenValue, blueValue] = matchedNumbers.map(Number)
    if (
      [redValue, greenValue, blueValue].some((value) => isNaN(value) || value < 0 || value > 255)
    ) {
      return ''
    }
    return formatRgba(redValue, greenValue, blueValue)
  } catch {
    return ''
  }
}
